import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Link as LinkIcon, Eye, EyeOff, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { generateApiKeyParts } from "@/lib/apiKeys";
import { logCrud } from "@/services/activities";

const profileSchema = z.object({
  nome_completo: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  telefone: z.string().optional(),
  cargo: z.string().optional(),
  data_nascimento: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export default function ProfileSettings() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [generatedUserKey, setGeneratedUserKey] = useState<string | null>(null);
  const [showGeneratedUserKey, setShowGeneratedUserKey] = useState<boolean>(true);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
      // Verificar se usuário tem Google vinculado
      const hasGoogle = user?.app_metadata?.providers?.includes('google') || 
                        user?.identities?.some(i => i.provider === 'google');
      setGoogleConnected(!!hasGoogle);
    });
  }, []);

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      nome_completo: "",
      telefone: "",
      cargo: "",
      data_nascimento: "",
    },
  });

  useEffect(() => {
    if (profile) {
      profileForm.reset({
        nome_completo: profile.nome_completo || "",
        telefone: profile.telefone || "",
        cargo: profile.cargo || "",
        data_nascimento: profile.data_nascimento || "",
      });
    }
  }, [profile, profileForm]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      if (!userId) throw new Error("Usuário não autenticado");
      
      const { error } = await supabase
        .from("profiles")
        .update({
          nome_completo: data.nome_completo,
          telefone: data.telefone || null,
          cargo: data.cargo || null,
          data_nascimento: data.data_nascimento || null,
        })
        .eq("id", userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      toast.success("Perfil atualizado!");
      if (userId) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "update", entityId: userId, screenSlug: "configuracoes/perfil" });
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar perfil: " + error.message);
    },
  });

  const handleLinkGoogle = async () => {
    const { error } = await supabase.auth.linkIdentity({
      provider: 'google',
    });

    if (error) {
      toast.error("Erro ao vincular conta Google: " + error.message);
    } else {
      toast.success("Conta Google vinculada com sucesso!");
    }
  };

  const handleUnlinkGoogle = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const googleIdentity = user?.identities?.find(i => i.provider === 'google');
    
    if (!googleIdentity) {
      toast.error("Conta Google não encontrada");
      return;
    }

    const { error } = await supabase.auth.unlinkIdentity(googleIdentity);

    if (error) {
      toast.error("Erro ao desvincular conta Google: " + error.message);
    } else {
      setGoogleConnected(false);
      toast.success("Conta Google desvinculada com sucesso!");
    }
  };

  return (
    <div className="space-y-6" data-cy="profile-settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Meu Perfil</h1>
        <p className="text-muted-foreground">
          Gerencie suas informações pessoais
        </p>
      </div>

      <Card data-cy="profile-card">
        <CardContent className="pt-4">
          <Form {...profileForm}>
            <form onSubmit={profileForm.handleSubmit((data) => updateProfileMutation.mutate(data))} className="space-y-4" data-cy="profile-form">
              <FormField
                control={profileForm.control}
                name="nome_completo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo</FormLabel>
                    <FormControl>
                      <Input placeholder="Seu nome completo" {...field} data-cy="profile-name-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={profileForm.control}
                  name="telefone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input placeholder="(00) 00000-0000" {...field} data-cy="profile-phone-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={profileForm.control}
                  name="cargo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cargo</FormLabel>
                      <FormControl>
                        <Input placeholder="Seu cargo" {...field} data-cy="profile-role-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={profileForm.control}
                  name="data_nascimento"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Nascimento</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} data-cy="profile-birthdate-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-md bg-muted p-3" data-cy="profile-email-display">
                <p className="text-sm text-muted-foreground mb-1">Email:</p>
                <code className="text-sm font-mono">{profile?.email}</code>
              </div>

              <Separator className="my-6" />

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Conta Google</h3>
                  <p className="text-sm text-muted-foreground">
                    Vincule sua conta Google para fazer login mais rápido
                  </p>
                </div>
                
                {googleConnected ? (
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <LinkIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Conta Google Conectada</p>
                        <p className="text-xs text-muted-foreground">
                          Você pode fazer login com sua conta Google
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnlinkGoogle}
                    >
                      Desvincular
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleLinkGoogle}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Vincular Conta Google
                  </Button>
                )}
              </div>

            <Button type="submit" disabled={updateProfileMutation.isPending} className="w-full" data-cy="profile-save-btn">
              {updateProfileMutation.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </form>
          </Form>
        </CardContent>
      </Card>

      {/* Minha API Key */}
      <Card data-cy="user-apikey-card">
        <CardHeader>
          <CardTitle>Minha API Key</CardTitle>
          <CardDescription>Gere sua chave pessoal para integrações</CardDescription>
        </CardHeader>
        <CardContent>
          {userId ? (
            <UserApiKeySection userId={userId} />
          ) : (
            <div className="text-sm text-muted-foreground">Faça login para gerar sua chave</div>
          )}

          {false && generatedUserKey && (
            <div />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserApiKeySection({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState<Record<string, boolean>>({});
  const { data: profile } = useQuery({
    queryKey: ["profile-for-apikey", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, tenant_id")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: keys } = useQuery({
    queryKey: ["api_keys", userId, "user"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("api_keys" as any)
        .select("id, name, enabled, key_prefix, key_full, created_at, last_used_at, expires_at")
        .eq("profile_id", userId)
        .eq("type", "user")
        .order("created_at", { ascending: false }) as any;
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const createKeyMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.tenant_id) throw new Error("Tenant não encontrado para o usuário");
      const { prefix, full, hash } = await generateApiKeyParts("user");
      const { error } = await supabase
        .from("api_keys" as any)
        .insert({
          tenant_id: profile.tenant_id,
          profile_id: userId,
          type: "user",
          key_hash: hash,
          key_prefix: prefix,
          key_full: full,
          name: "Minha API Key",
          enabled: true,
          scopes: { "*": ["read", "create", "update"] },
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", userId, "user"] });
      toast.success("API Key gerada!");
    },
    onError: (error: any) => {
      toast.error("Erro ao gerar chave: " + error.message);
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: async (payload: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("api_keys" as any)
        .update({ enabled: payload.enabled })
        .eq("id", payload.id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", userId, "user"] });
      toast.success(variables.enabled ? "Chave ativada!" : "Chave desativada!");
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar chave: " + error.message);
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("api_keys" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", userId, "user"] });
      toast.success("API Key excluída");
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir chave: " + error.message);
    },
  });

  return (
    <div className="space-y-3" data-cy="user-apikey-section">
      <Button onClick={() => createKeyMutation.mutate()} disabled={createKeyMutation.isPending} data-cy="user-apikey-generate-btn">
        {createKeyMutation.isPending ? "Gerando..." : "Gerar Minha API Key"}
      </Button>

      <div className="space-y-2">
        {keys?.length ? (
          keys.map((k: any) => (
            <div key={k.id} className="flex items-center justify-between rounded-md border p-3" data-cy="user-apikey-item">
              <div>
                <p className="text-sm font-medium">{k.name || "Minha Chave"}</p>
                <div className="mt-2">
                  <code className="text-xs font-mono break-all">{showMap[k.id] ? k.key_full : "••••••••••"}</code>
                </div>
              <p className="text-xs text-muted-foreground">Criada: {new Date(k.created_at).toLocaleString()}</p>
                {k.last_used_at && (
                  <p className="text-xs text-muted-foreground">Último uso: {new Date(k.last_used_at).toLocaleString()}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setShowMap((m) => ({ ...m, [k.id]: !m[k.id] }))}>
                  {showMap[k.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(k.key_full);
                    toast.success("Chave copiada!");
                  }}
                  title="Copiar"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleEnabledMutation.mutate({ id: k.id, enabled: !k.enabled })}
                >{k.enabled ? "Desativar" : "Ativar"}</Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { setPendingDeleteId(k.id); setConfirmOpen(true); }}
                >Excluir</Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-sm text-muted-foreground" data-cy="user-apikey-empty">Nenhuma chave criada ainda</div>
        )}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Tem certeza que deseja excluir esta chave? Esta ação não pode ser desfeita.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (pendingDeleteId) deleteKeyMutation.mutate(pendingDeleteId);
                  setConfirmOpen(false);
                  setPendingDeleteId(null);
                }}
              >Excluir</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {false && (
        <div />
      )}
    </div>
  );
}

